/**
 * `UnifiedPhaseList` — the mobile (`<md`) layout for the Live Agent
 * view (ADR-0163 §4). One list, one scroll surface: each phase renders
 * narration + human/code call card + inline tier-gated
 * `MandateSigningCard` as a single unit, replacing the stacked
 * two-column layout whose dueling internal scroll cages made
 * interleaved reading impossible on a phone.
 *
 * The card body honors the global view mode: `'human'` renders the
 * `HumanCallCard` with the narration OUTSIDE the card (so the story
 * reads once, `hideOutcome`), `'code'` renders the raw
 * `CodeCallCard`. The honesty tags render in both (ADR-0163 §7).
 */

import { motion } from 'framer-motion';
import { Server } from 'lucide-react';

import { Card } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import type { Phase } from '../journeys/types.js';
import type { AgentDef, VerificationTier } from '../roster.js';
import type { Fact } from '../lib/facts.js';
import type { ViewMode } from '../hooks/useViewMode.js';
import { collapseSkipped } from './AgentColumn.js';
import { CodeCallCard } from './CodeCallCard.js';
import { HumanCallCard } from './HumanCallCard.js';
import { MandateSigningCard, mandateKindFor } from './MandateSigningCard.js';
import { ResetIconButton } from './ResetIconButton.js';
import { ViewToggle } from './ViewToggle.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface UnifiedPhaseListProps {
  agent: AgentDef;
  phases: Phase[];
  step: number;
  tier: VerificationTier;
  mode: ViewMode;
  onChangeMode: (mode: ViewMode) => void;
  factsFor: (phase: Phase) => readonly Fact[];
  canReset: boolean;
  onReset: () => void;
}

export function UnifiedPhaseList({ agent, phases, step, tier, mode, onChangeMode, factsFor, canReset, onReset }: UnifiedPhaseListProps): JSX.Element {
  const C = useC();
  const visiblePhases = collapseSkipped(phases.slice(0, step + 1));

  return (
    <Card className="p-4">
      {/* Header row — 1:1 mirror of the desktop EndpointColumn header
          (icon tile + "Sill edge" + right-aligned ViewToggle). The
          toggle lives HERE on mobile, not in the sticky strip: it's a
          set-once persisted preference and the third sticky row read
          as clutter (founder feedback 2026-07-05). One toggle locus
          per viewport, same visual role. */}
      <div className="flex items-center gap-3 mb-4 pb-4 flex-wrap" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-center" style={{ width: 32, height: 32, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}>
          <Server size={16} color={C.limeFg} />
        </div>
        <div className="flex-1 min-w-0">
          <span style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 500 }}>Sill edge</span>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 2 }}>edge.sill.so · live discovery + illustration</div>
        </div>
        <ResetIconButton canReset={canReset} onReset={onReset} />
        <ViewToggle mode={mode} onChange={onChangeMode} />
      </div>
      <div className="space-y-4" data-testid="unified-phase-list">
        {visiblePhases.map((p, i) => {
          const mandate = mandateKindFor(p.id);
          const isLatest = i === visiblePhases.length - 1;
          if (p.skipped) {
            return (
              <div key={p.id} className="flex gap-3">
                <div className="flex-shrink-0" style={{ width: 4, background: C.dim, borderRadius: 1 }} />
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1, marginBottom: 4 }}>{p.name.toUpperCase()}</div>
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim, fontStyle: 'italic' }}>
                    Skipped — the agent didn't get past the identity check.
                  </div>
                </div>
              </div>
            );
          }
          return (
            <motion.div key={p.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="flex gap-3">
              <div className="flex-shrink-0" style={{ width: 4, background: p.ok ? C.limeFg : C.coralFg, borderRadius: 1 }} />
              <div className="flex-1 min-w-0 space-y-2">
                <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{p.narration}</div>
                {mode === 'human' ? (
                  <HumanCallCard phase={p} facts={factsFor(p)} hideOutcome />
                ) : (
                  <CodeCallCard phase={p} />
                )}
                {mandate && <MandateSigningCard mandate={mandate} agent={agent} animate={isLatest} tier={tier} />}
              </div>
            </motion.div>
          );
        })}
        {step < 0 && (
          <div className="text-center py-12" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Press <span style={{ color: C.text }}>Run</span> to start.
          </div>
        )}
      </div>
    </Card>
  );
}
